import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The availability sync's three switches, in one place.
 *
 * They live in three different stores — a Medplum `Subscription`, a `task` row, and that task's
 * `scheduled_args` — and until now the only way to see or set the first was the Medplum admin app,
 * a different tool with a different login. Nobody could answer "is the sync on?" without opening
 * two systems, which is a poor state for a feature whose failure mode is silence.
 *
 * **Reading is `@ZocdocOpsAuth()`, writing is `@SuperAdminAuth()`.** A care coordinator works the
 * Zocdoc console all day and should be able to see whether publishing is live — that is
 * operational context for everything else on the page. Turning it on is not theirs: each switch
 * commits the practice to writing a doctor's calendar on a third-party marketplace.
 *
 * No FHIR at the boundary: the Subscription crosses as a status string, never as a resource.
 */
export const ZOCDOC_SYNC_SUBSCRIPTION_STATUSES = ['off', 'active'] as const
export const ZOCDOC_SYNC_TASK_STATUSES = ['disabled', 'active'] as const

const syncStateSchema = z.object({
  subscription: z.object({
    /**
     * `absent` means no Subscription exists — `project-configure` skips creating one without
     * `MIDDLEWARE_PUBLIC_URL` and `ZOCDOC_SLOT_SUBSCRIPTION_SECRET`, so this is the ordinary state
     * of a local or unconfigured environment rather than an error. `error` is Medplum's own: it
     * disables a subscription after repeated delivery failures.
     */
    status: z.enum(['absent', 'off', 'active', 'requested', 'error']),
    /** Why it cannot be turned on, when it cannot. */
    blockedReason: z.string().optional(),
  }),
  task: z.object({
    status: z.enum(['absent', 'disabled', 'active', 'paused']),
    /** So the card can say "last ran 20 minutes ago", not just whether it is switched on. */
    lastRunAt: z.string().optional(),
    lastRunStatus: z.string().optional(),
    lastRunSummary: z.string().optional(),
  }),
  publishing: z.object({
    /**
     * True = runs build and check but send nothing. The second safety: a task can be active while
     * everything it does is still a rehearsal.
     */
    dryRun: z.boolean(),
  }),
})

export const zocdocSyncState = oc
  .route({ method: 'GET', path: '/v2/zocdoc/sync-state' })
  .output(syncStateSchema)

export const zocdocSetSyncControls = oc
  .route({ method: 'PUT', path: '/v2/zocdoc/sync-controls' })
  .input(
    z.object({
      /** Omitted leaves a switch alone, so the card can send only what changed. */
      subscriptionStatus: z.enum(ZOCDOC_SYNC_SUBSCRIPTION_STATUSES).optional(),
      taskStatus: z.enum(ZOCDOC_SYNC_TASK_STATUSES).optional(),
      dryRun: z.boolean().optional(),
    }),
  )
  .output(syncStateSchema)
